import { resolveTheme } from '@/shared/theme/theme';
import type { Tenant, TenantConfig } from '../domain/entities/tenant.entity';
import type { TenantEdition } from '../domain/entities/tenant.entity';
import {
  isTenantId,
  tenantEnvKey,
  type TenantId,
} from '../domain/entities/tenant-ids';
import { EXAM_EDITIONS } from './editions.mapper';
import { buildDefaultContent } from './default-content';

/**
 * A configuração de cada prova. É um `Record` sobre `TenantId`, então prova
 * nova em `TENANT_IDS` sem entrada aqui quebra o typecheck.
 *
 * Para mudar a cara de uma prova, declare `theme` com só os tokens que mudam
 * (ver `ThemeTokens`); para mudar texto, `content` com só as seções que mudam.
 * O resto vem do padrão. As questões de cada simulado não ficam aqui: moram
 * no `question-ids.mapper.ts`. Exemplo:
 *
 * ```ts
 * hcpa: {
 *   exam: { … },
 *   theme: { accent: '#7ee2b8', accentStrong: '#1fa774' },
 *   content: { youtubeVideoId: 'abc123' },
 * },
 * ```
 */
const TENANT_CONFIGS: Record<TenantId, TenantConfig> = {
  amrigs: {
    exam: {
      shortName: 'AMRIGS',
      fullName: 'Associação Médica do Rio Grande do Sul',
    },
  },
  'psu-mg': {
    exam: {
      shortName: 'PSU-MG',
      fullName: 'Processo Seletivo Unificado de Minas Gerais',
    },
  },
  scmbh: {
    exam: {
      shortName: 'SCMBH',
      fullName: 'Santa Casa de Misericórdia de Belo Horizonte',
    },
  },
  hcpa: {
    exam: {
      shortName: 'HCPA',
      fullName: 'Hospital de Clínicas de Porto Alegre',
    },
  },
};

/**
 * A edição que o build serve, de `VITE_EDITION_<PROVA>`. **Lança** se o ano
 * não vier ou não existir no `EXAM_EDITIONS`: uma LP no ar com a edição errada
 * mandaria respostas para a prova errada, e falhar ao subir é melhor. O
 * `vite.config.ts` já recusa ano ausente no dev e no build.
 */
export function readEditionYear(id: TenantId): number {
  const key = tenantEnvKey('VITE_EDITION', id);
  const year = Number.parseInt(String(import.meta.env[key] ?? ''), 10);
  if (!EXAM_EDITIONS[id][year]) {
    throw new Error(`${key} sem edição cadastrada em EXAM_EDITIONS: ${year}`);
  }
  return year;
}

export function getTenant(
  id: TenantId,
  year: number = readEditionYear(id),
): Tenant {
  const config = TENANT_CONFIGS[id];
  const editionConfig = EXAM_EDITIONS[id][year];
  if (!editionConfig) throw new Error(`Edição ${year} não cadastrada: ${id}`);

  const edition: TenantEdition = {
    year,
    ...editionConfig,
    officialTemplateId: String(
      import.meta.env[tenantEnvKey('VITE_OFFICIAL_EXAM_TEMPLATE_ID', id)] ?? '',
    ).trim(),
    // Os PDFs da pasta `materiais/` deste build, que o `vite.config.ts`
    // anuncia (vazio = não há o arquivo).
    ensaioPdfPath: import.meta.env.VITE_MATERIAL_ENSAIO_PDF || undefined,
    previsoesPdfPath: import.meta.env.VITE_MATERIAL_PREVISOES_PDF || undefined,
    gabaritoPdfPath: import.meta.env.VITE_MATERIAL_GABARITO_PDF || undefined,
  };

  return {
    id,
    exam: config.exam,
    theme: resolveTheme(config.theme),
    edition,
    content: {
      ...buildDefaultContent(config.exam, edition),
      ...config.content,
    },
  };
}

/**
 * A prova deste build, de `VITE_TENANT`. O `vite.config.ts` já recusa um valor
 * inválido no dev e no build; a checagem aqui cobre quem importar isto por
 * outro caminho (um teste, por exemplo) sem a variável.
 */
export function getCurrentTenant(): Tenant {
  const id = import.meta.env.VITE_TENANT;
  if (!isTenantId(id)) {
    throw new Error(`VITE_TENANT inválido ou ausente: ${id ?? 'nada'}`);
  }
  return getTenant(id);
}
